<template>
  <div style="min-height: 570px; position: relative; display: flex; flex-direction: column; height: 100%; flex: 1;"
    :class="{ 'fullscreen': fullscreen }" @click="activateKeyboardNavigation"
    v-click-outside="deactivateKeyboardNavigation">

    <div v-if="isEditing"
      style="position: fixed; bottom: 20px; left: 0; right: 0; z-index: 100; display: flex; justify-content: center; align-items: center; width: 100%;">
      <v-btn @click="toggleEditMode" class="rounded-xl cancel-btn" height="36px" small depressed>
        Cancelar
      </v-btn>
      <v-btn color="primary" @click="saveFlashcards" class="rounded-xl primary-btn" height="36px" small depressed>
        Guardar cambios
      </v-btn>
    </div>


    <div class="flashcards-container">
      <div v-if="isLoading" class="text-center">
        <v-skeleton-loader type="card" class="mx-auto mt-12" width="400" height="400"></v-skeleton-loader>
      </div>

      <div v-else-if="error" class="text-center my-6">
        <v-alert type="error" outlined>
          {{ error }}
        </v-alert>
        <v-btn color="primary" class="mt-4" @click="fetchFlashcards">
          Intentar de nuevo
        </v-btn>
      </div>

      <div v-else-if="flashcards.length === 0" class="text-center my-6">
        <v-alert color="#458553" style="color: white" class="rounded-lg">
          No se encontraron tarjetas de memoria para este documento.
        </v-alert>
        <v-btn color="primary" class="mt-4 rounded-lg expand-button" @click="generateFlashcards">
          Generar tarjetas de memoria
        </v-btn>
      </div>

      <div v-else class="my-auto" style="position: relative;">
        <!-- Botón de edición -->
        <div class="d-flex justify-end">
          <v-btn v-if="true || !fullscreen" color="#E8E8E8" text-color="#6E8071" @click="toggleEditMode"
            class="edit-btn rounded-xl" height="36px" small depressed>
            <svg v-if="!isEditing" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
              fill="none" stroke="#6E8071" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
              class="lucide lucide-pencil-icon lucide-pencil">
              <path
                d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
              <path d="m15 5 4 4" />
            </svg>

            <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="#6E8071" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
              class="lucide lucide-x-icon lucide-x">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </v-btn>
        </div>

        <!-- Vista Principal: S'amaga quan l'estudi està complet -->
        <div v-if="!isStudyComplete">
          <!-- Vista de lista -->
          <div v-if="viewMode === 'list'" class="flashcards-list" style="position: relative !important;">


            <v-card flat class="mb-4 shadow-sm rounded-lg px-5 py-4" style="border: 1px solid #E6E6E6;"
              v-for="(card, index) in flashcards" :key="index">
              <v-row no-gutters>
                <v-col cols="6" class="d-flex align-start justify-start text-start">
                  <div v-if="isEditing" class="w-100">
                    <v-textarea v-model="card.question" rows="3" auto-grow outlined dense hide-details
                      placeholder="Pregunta" class="edit-textarea"></v-textarea>
                  </div>
                  <span v-else class="font-rubik">{{ card.question }}</span>
                </v-col>
                <v-col cols="6" class="d-flex align-start justify-start text-start">
                  <v-divider class="mx-8" vertical></v-divider>
                  <div v-if="isEditing" class="w-100">
                    <v-textarea v-model="card.answer" rows="3" auto-grow outlined dense hide-details
                      placeholder="Respuesta" class="edit-textarea"></v-textarea>
                  </div>
                  <span v-else class="font-rubik">{{ card.answer }}</span>
                </v-col>
              </v-row>
              <div v-if="isEditing" class="d-flex justify-end mt-2">
                <v-btn small text color="error" @click="deleteFlashcard(index)" class="delete-btn">
                  <v-icon left small>mdi-delete</v-icon>
                  Eliminar
                </v-btn>
              </div>
            </v-card>
          </div>

          <!-- Vista de tarjetas -->
          <div v-else>

            <!-- Progress bar -->
            <div v-if="flashcards.length > 0" class="progress-bar"
              :style="{ position: 'fixed', top: '60px', left: '0', right: '0', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', width: `calc(100vw - ${navWidth + 15}px)` }"
              >
              <v-progress-linear :value="completionProgress" color="primary" height="8"></v-progress-linear>
                
            </div>

            <!-- Counters -->
            <div style="width: 100%; padding: 0 20px;">
              
              <div class="counters-container">
                <div class="counter incorrect-counter">
                  <v-icon small color="#F44336" class="mr-1">mdi-close</v-icon>
                  {{ incorrectCount }}
                </div>
                
                <div class="counter total-counter">
                  <v-icon small color="#808080" class="mr-1">mdi-cards</v-icon>

                  {{ currentIndex + 1 }} / {{ flashcards.length }}
                </div>

                <div class="counter correct-counter">
                  <v-icon small color="#4CAF50" class="mr-1">mdi-check</v-icon>
                  {{ correctCount }}
                </div>
              </div>
            </div>



            <!-- Tarjeta actual -->
            <div class="flashcard-wrapper">
              <transition :name="slideDirection === 'next' ? 'slide-next' : 'slide-prev'" mode="out-in">
                <div 
                :show="!goingToNextCard"
                :key="currentIndex" color="transparent" style="height: calc(100vh - 200px);"
                  class="flashcard no-background-hover" 
                  :class="{ 
                    'pa-10': !$vuetify.breakpoint.xs
                  }"
                  :ripple="false"
                  @click="flipCard">
                  
                  <div class="flashcard-inner" :class="{ 'flipped': isFlipped }">
                    <div class="flashcard-front">
                      <div class="difficulty-indicator" :class="currentCard.level"></div>
                      <v-chip v-if="presentationCountForCurrentCard > 1" small color="blue-grey lighten-4" text-color="blue-grey darken-2" class="presentation-chip">
                        {{ presentationCountForCurrentCard -1 }} intento{{ presentationCountForCurrentCard -1 > 1 ? 's' : '' }}
                      </v-chip>
                      <div class="flashcard-content">
                        <p v-if="!cardActionAnimation && !goingToNextCard">{{ currentCard.question }}</p>
                      </div>
                      <div v-if="!clicked && !isEditing" class="flashcard-footer">
                        <v-icon small color="grey">mdi-gesture-tap</v-icon>
                        <span class="ml-1">Toca para ver respuesta</span>
                      </div>
                    </div>

                    <div class="flashcard-back">
                      <v-chip v-if="presentationCountForCurrentCard > 1" small color="blue-grey lighten-4" text-color="blue-grey darken-2" class="presentation-chip">
                        {{ presentationCountForCurrentCard - 1 }} intento{{ presentationCountForCurrentCard - 1 > 1 ? 's' : '' }}
                      </v-chip>
                      <div class="flashcard-content">
                        <p v-if="!cardActionAnimation && !goingToNextCard">{{ currentCard.answer }}</p>
                      </div>
                      <div v-if="!clicked && !isEditing" class="flashcard-footer">
                        <v-icon small color="grey">mdi-gesture-tap</v-icon>
                        <span class="ml-1">Toca para ver pregunta</span>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
              
              <!-- Botones de evaluación debajo de la tarjeta -->
              <div v-if="!isEditing" class="evaluation-buttons-container pb-2"
              style="position: absolute; bottom: 0; left: 0; right: 0; z-index: 100; display: flex; justify-content: center; align-items: center; width: 100%;"
              >
                <v-btn color="error" @click.stop="markAsIncorrect" class="evaluation-btn error-btn">
                  <v-icon left small>mdi-close</v-icon>
                  Preguntar de nuevo
                </v-btn>
                <v-btn color="success" @click.stop="markAsCorrect" class="evaluation-btn success-btn"
                  style="padding-right: 24px !important;">
                  <v-icon left small>mdi-check</v-icon>
                  ¡Correcto!
                </v-btn>
              </div>

              <div v-else style="height: 44px;"></div>
            </div>


            <!-- Navegación -->
            <div v-if="false && flashcards.length > 0" class="navigation-controls"
              :style="{ position: 'fixed', bottom: '20px', left: '0', right: '0', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', width: `calc(100vw - ${navWidth}px)` }">
              <v-btn fab color="primary" :disabled="currentIndex === 0" @click="prevCard"
                class="navigation-btn navigation-btn-prev">
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>

              <span class="card-counter">{{ currentIndex + 1 }} / {{ filteredFlashcards.length }}</span>

              <v-btn fab color="primary" :disabled="currentIndex === filteredFlashcards.length - 1" @click="nextCard"
                class="navigation-btn navigation-btn-next">
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>

            <!--  keyboard hint -->
            <div v-if="!clicked && !isEditing" class="keyboard-hint justify-center text-center"
              :style="{ position: 'absolute', top: '80px', left: '0', right: '0', zIndex: 100, display: 'flex', justifyContent: 'center' }">
              <v-icon small color="grey" class="mr-1">mdi-gesture-tap</v-icon>
              <span>Toca la tarjeta para voltearla</span>
            </div>
            <div v-if="isFlipped && !clicked && !isEditing" class="swipe-hint text-center" 
              :style="{ position: 'absolute', top: '120px', left: '0', right: '0', zIndex: 100}">
              <v-icon small color="grey" class="mr-1">mdi-gesture-tap</v-icon>
              <span><strong>Usa los botones para evaluar:</strong></span>
              <span class="swipe-left-hint pa-1 rounded-sm ml-1" style="background-color: #F44336;">
                <span style="color: white">Incorrecta</span>
              </span>
              <span class="swipe-right-hint pa-1 rounded-sm ml-1" style="background-color: #4CAF50;">
                <span style="color: white">Correcta</span>
              </span>
            </div>

     
            <!-- Botón para eliminar tarjeta actual en modo edición -->
            <div v-if="isEditing" class="d-flex justify-center mt-4">
              <v-btn color="error" outlined @click="deleteCurrentFlashcard" class="delete-btn">
                <v-icon left>mdi-delete</v-icon>
                Eliminar tarjeta actual
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Vista de Resum: Es mostra quan l'estudi està complet -->
        <div v-if="isStudyComplete" class="study-summary">
          <v-card flat class="mb-6 shadow-sm rounded-lg pa-6 text-center summary-card-container" style="border: 1px solid #E6E6E6;">
            <h2 class="mb-2 font-rubik" style="font-size: 1.4rem; font-weight: 600;">Resumen del Estudio</h2>

            <!-- Gràfic Percentatge Primer Intent -->
            <div class="mb-4">
              <h3 class="mb-2 font-rubik" style="font-size: 1.0rem; font-weight: 500;">Correctas al Primer Intento</h3>
               <v-progress-circular
                 :rotate="-90"
                 :size="90"
                 :width="10"
                 :value="firstTryCorrectPercentage"
                 color="#4CAF50"
                 class="mx-auto"
               >
                 <span style="font-size: 1.1rem; font-weight: bold;">{{ Math.round(firstTryCorrectPercentage) }}%</span>
               </v-progress-circular>
            </div>

            <v-divider class="my-4"></v-divider>

            <!-- Llista de Repàs -->
            <h3 class="mb-3 font-rubik" style="font-size: 1.0rem; font-weight: 500;">Repaso de Tarjetas</h3>
            <div class="flashcards-summary-list">
               <v-card flat class="mb-4 shadow-sm rounded-lg px-5 py-4 summary-card"
                 style="border: 1px solid #E6E6E6;"
                 :class="{ 'incorrect-summary-card': failureCounts[card.id || flashcards.indexOf(card)] > 0 }"
                 v-for="(card, index) in summaryCards" :key="'summary-' + (card.id || index)">
                <div class="d-flex flex-column text-start" >
                    <span class="font-rubik">{{ card.question }}</span>
                  <v-divider class="my-4"></v-divider>
                    <div>
                      <span class="font-rubik">{{ card.answer }}</span>
                      <v-chip v-if="failureCounts[card.id || flashcards.indexOf(card)] > 0" small
                        class="grey-darken-1--text"
                        color="grey-lighten-1" style="position: absolute; right: 10px; top: 10px;">
                        {{ failureCounts[card.id || flashcards.indexOf(card)] }} 
                        {{ failureCounts[card.id || flashcards.indexOf(card)] === 1 ? 'fallo' : 'fallos' }}
                      </v-chip>
                    </div>
                </div>
              </v-card>
            </div>

            <v-divider class="my-4"></v-divider>

            <div class="d-flex justify-space-between">
            <!-- Botó de Reiniciar -->
            <v-btn color="grey-lighten-1" 
            style="  box-shadow: 0 4px 0 rgb(193 193 193) !important;"
            class="mt-4 rounded-lg" @click="restartStudy">
              <v-icon left>mdi-refresh</v-icon>
              Volver a estudiar
            </v-btn>

            <!-- Següent secció -->
            <v-btn color="primary" class="mt-4 rounded-lg expand-button" @click="$emit('navigate-to-next-block')">
              Siguiente
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
          </v-card>
          
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FlashcardsView',
  props: {
    documentId: {
      type: [String, Number],
      required: true
    },
    blockId: {
      type: [String, Number],
      required: true
    },
    fullscreen: {
      type: Boolean,
      default: false
    },
    initialIndex: {
      type: Number,
      default: 0
    },
    viewMode: {
      type: String,
      default: 'cards'
    },
    navWidth: {
      type: Number,
      default: 400 // Default width if not provided
    }
  },
  data() {
    return {
      documentTitle: '',
      flashcards: [],
      isLoading: true,
      clicked: false,
      error: null,
      selectedDifficulty: 'Todas',
      difficultyOptions: [
        'Todas',
        'easy',
        'medium',
        'hard'
      ],
      currentIndex: 0,
      isFlipped: false,
      slideDirection: 'next',
      isEditing: false,
      originalFlashcards: [], // Para guardar el estado original antes de editar
      isKeyboardNavigationActive: false, // Controla si la navegación por teclado está activa
      answeredCards: {}, // Para llevar un registro de las tarjetas respondidas correctamente
      remainingCards: [], // Para mantener el orden de las tarjetas pendientes
      correctCount: 0, // Contador de respuestas correctas
      incorrectCount: 0, // Contador de respuestas incorrectas
      flippedCards: {}, // Para rastrear las tarjetas que han sido volteadas
      isDragging: false,
      startX: 0,
      currentX: 0,
      swipeThreshold: 20, // Reducir el umbral para un swipe válido
      swipeDirection: null,
      showSwipeHelp: true, // Control para mostrar ayuda de swipe solo la primera vez
      hasMoved: false, // Track significant movement during drag
      dragStartTime: 0, // Time drag started
      clickThreshold: 10, // Maximum movement allowed for a click
      clickDurationThreshold: 200, // Maximum duration for a click (ms)
      cardActionAnimation: null, // Can be 'slideOutLeft', 'slideOutRight'
      goingToNextCard: false,
      presentationCounts: {}, // Contador de veces que se ha preguntado cada tarjeta
      isStudyComplete: false, // Indica si se ha completado el estudio
      firstTryCorrect: {}, // { cardId: boolean } - Rastrea el éxito en el primer intento
      incorrectlyAnsweredCards: new Set(), // Set de IDs de tarjetas respondidas incorrectamente al menos una vez
      // Nuevas propiedades para guardar/cargar resultados:
      localStorageKey: '', // Para almacenar la clave de localStorage
      hasStoredResults: false, // Indica si hay resultados guardados
      resultData: null, // Para almacenar los resultados cargados
      failureCounts: {}, // Nuevo: Mapa para contar los fallos por tarjeta { cardId: número de fallos }
    };
  },
  computed: {
    filteredFlashcards() {
      if (this.selectedDifficulty === 'Todas') {
        return this.flashcards;
      }
      return this.flashcards.filter(card => card.level === this.selectedDifficulty);
    },
    currentCard() {
      return this.filteredFlashcards.length > 0
        ? this.filteredFlashcards[this.currentIndex]
        : { question: 'No hay tarjetas disponibles', answer: '', level: 'medium' };
    },
    // Computar el progreso de tarjetas completadas correctamente
    completionProgress() {
      if (this.filteredFlashcards.length === 0) return 0;
      // Contar cuántas tarjetas han sido respondidas correctamente
      const correctlyAnswered = Object.keys(this.answeredCards).length;
      // Calcular porcentaje basado en tarjetas respondidas correctamente sobre el total
      return (correctlyAnswered / this.filteredFlashcards.length) * 100;
    },
    // Calcula el percentatge de targetes correctes al primer intent
    firstTryCorrectPercentage() {
      const totalCards = this.flashcards.length;
      if (totalCards === 0) return 0;
      const firstTryAttempts = Object.keys(this.firstTryCorrect).length;
      const correctFirstTry = Object.values(this.firstTryCorrect).filter(correct => correct).length;
      // Considera només les targetes per les quals hi ha hagut un primer intent
      // return firstTryAttempts > 0 ? (correctFirstTry / firstTryAttempts) * 100 : 0;
      // O, si vols el percentatge sobre el total de targetes:
       return (correctFirstTry / totalCards) * 100;
    },
    // Ordena les targetes per al resum, primer les que más han fallado
    summaryCards() {
      return [...this.flashcards].sort((a, b) => {
        const aId = a.id || this.flashcards.indexOf(a);
        const bId = b.id || this.flashcards.indexOf(b);
        const aFailures = this.failureCounts[aId] || 0;
        const bFailures = this.failureCounts[bId] || 0;
        // Ordenar de mayor a menor número de fallos
        return bFailures - aFailures;
      });
    },
    swipeStyle() {
      // If animating out, let the CSS class handle everything (including transform reset)
      if (this.cardActionAnimation) { // Changed from isAnimatingOut
        return {}; // Don't apply any inline transform or transition
      }
      // Apply transform only if dragging and moved
      if (this.isDragging && this.hasMoved) {
        const translateX = (this.currentX - this.startX) * 1.5;
        const rotate = translateX * 0.05;
        return {
          transform: `translateX(${translateX}px) rotate(${rotate}deg)`,
          transition: 'none', // No transition during active drag
        };
      }
      // Default state: allow smooth transition back to center after drag ends
      return {
        transition: 'transform 0.3s ease-out',
      };
    },
    presentationCountForCurrentCard() {
      if (!this.currentCard) return 0;
      const cardId = this.currentCard.id || this.currentIndex; // Use ID if available, fallback to index
      return this.presentationCounts[cardId] || 0;
    },
    // Nueva propiedad calculada para la clave de localStorage
    storageKey() {
      if (!this.documentId || !this.blockId) {
        console.warn('DocumentId o BlockId no están disponibles para generar la clave de localStorage.');
        return `flashcards_results_fallback_${Date.now()}`;
      }
      return `flashcards_results_doc_${this.documentId}_block_${this.blockId}`;
    }
  },
  created() {
    this.fetchFlashcards();
    // Agregar event listeners para las teclas
    window.addEventListener('keydown', this.handleKeyDown);
    // Establecer el índice inicial si se proporciona
    this.currentIndex = this.initialIndex;
    // Initial count for the first card
    this.incrementPresentationCount(this.initialIndex);
    // Generar la clave de localStorage
    this.localStorageKey = this.storageKey;
  },
  beforeDestroy() {
    // Limpiar event listeners
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  watch: {
    selectedDifficulty() {
      // Resetear a la primera tarjeta cuando cambia el filtro
      this.currentIndex = 0;
      this.isFlipped = false;
      this.presentationCounts = {}; // Reset counts on filter change
      this.incrementPresentationCount(0); // Count the first card of the new filter
    },
    currentIndex(newIndex, oldIndex) {
      this.clicked = true;
      // Emitir el evento cuando cambia el índice actual
      this.$emit('index-change', newIndex);
      // Increment count for the *new* card being displayed
      this.incrementPresentationCount(newIndex);
    },
    initialIndex(val) {
      // Actualizar el índice cuando cambia la propiedad initialIndex
      if (val !== this.currentIndex) {
        this.currentIndex = val;
      }
    },
    // Añadir watchers para documentId y blockId
    documentId() {
      // Actualizar la clave de localStorage antes de resetear y cargar
      this.localStorageKey = this.storageKey;
      // Resetear estado y cargar nuevas flashcards cuando cambia el documento
      this.resetState();
      this.fetchFlashcards();
    },
    blockId() {
      // Actualizar la clave de localStorage antes de resetear y cargar
      this.localStorageKey = this.storageKey;
      // Resetear estado y cargar nuevas flashcards cuando cambia el bloque
      this.resetState();
      this.fetchFlashcards();
    }
  },
  methods: {
    incrementPresentationCount(index) {
      if (this.filteredFlashcards.length === 0 || index < 0 || index >= this.filteredFlashcards.length) {
         console.warn("Attempted to increment count for invalid index:", index);
         return;
      }
      const card = this.filteredFlashcards[index];
      // Ensure card exists before trying to access properties
      if (!card) {
          console.warn("Card not found at index:", index);
          return;
      }
      const cardId = card.id || index; // Prefer ID, fallback to index
      const currentCount = this.presentationCounts[cardId] || 0;
      this.$set(this.presentationCounts, cardId, currentCount + 1);
      // console.log(`Presentation count for card ${cardId}: ${this.presentationCounts[cardId]}`);
    },
    async fetchFlashcards() {
      this.isLoading = true;
      this.error = null;
      this.presentationCounts = {}; // Reset counts

      try {
        const response = await this.axios.get(`/blocks/${this.blockId}/flashcards`, {
          headers: {
            Authorization: this.$root.token.value
          }
        });

        if (response.data.success) {
          this.flashcards = response.data.flashcards;
          this.documentTitle = response.data.documentTitle || 'Tarjetas de memoria';
          // Inicializar remainingCards con los índices de todas las tarjetas
          this.remainingCards = [...Array(this.flashcards.length).keys()];
          this.answeredCards = {};
          this.flippedCards = {}; // Reset flipped status
          this.correctCount = 0;
          this.incorrectCount = 0;
          this.failureCounts = {}; // Reiniciar contador de fallos
          this.incrementPresentationCount(this.currentIndex); // Count initial card
          // Reset summary state
          this.isStudyComplete = false;
          this.firstTryCorrect = {};
          this.incorrectlyAnsweredCards = new Set();
          
          // Verificar si hay resultados guardados
          this.checkSavedResults();
          
          // Verificación adicional después de cargar para asegurar que se muestre el resumen
          // si todas las flashcards están respondidas
          this.$nextTick(() => {
            if (this.hasStoredResults && 
                Object.keys(this.answeredCards).length === this.flashcards.length && 
                this.flashcards.length > 0) {
              this.isStudyComplete = true;
            }
          });
        } else {
          this.error = response.data.error || 'No se pudieron cargar las tarjetas de memoria';
        }
      } catch (err) {
        console.error('Error al cargar las tarjetas de memoria:', err);
        this.error = 'Error al cargar las tarjetas de memoria. Por favor, inténtalo de nuevo.';
      } finally {
        this.isLoading = false;
      }
    },

    async generateFlashcards() {
      this.isLoading = true;
      this.error = null;
      this.presentationCounts = {}; // Reset counts

      try {
        const response = await this.axios.post(`/blocks/${this.blockId}/generate-flashcards`, {
          documentId: this.documentId,
          user_id: this.$root.currentUser.userId
        }, {
          headers: {
            Authorization: this.$root.token.value
          }
        });

        if (response.data.success) {
          this.flashcards = response.data.flashcards;
          this.documentTitle = response.data.fileName || 'Tarjetas de memoria';
          this.remainingCards = [...Array(this.flashcards.length).keys()]; // Reset state
          this.answeredCards = {};
          this.flippedCards = {};
          this.correctCount = 0;
          this.incorrectCount = 0;
          this.currentIndex = 0;
          this.isFlipped = false;
          this.incrementPresentationCount(0); // Count initial card after generation
          this.$toast.success('Tarjetas de memoria generadas correctamente');
          // Reset summary state
          this.isStudyComplete = false;
          this.firstTryCorrect = {};
          this.incorrectlyAnsweredCards = new Set();
          this.$nextTick(() => {
            this.incrementPresentationCount(this.currentIndex); // Recount current card if list not empty
          });
        } else {
          this.error = response.data.error || 'No se pudieron generar las tarjetas de memoria';
          this.$toast.error(this.error);
        }
      } catch (err) {
        console.error('Error al generar las tarjetas de memoria:', err);
        this.error = 'Error al generar las tarjetas de memoria. Por favor, inténtalo de nuevo.';
        this.$toast.error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    flipCard() {
      this.isFlipped = !this.isFlipped;
      const cardId = this.currentCard.id || this.currentIndex;
      if (!this.flippedCards[cardId] && this.isFlipped) { // Only mark as flipped when going to back
        this.$set(this.flippedCards, cardId, true);
      }
      this.clicked = true;
    },

    nextCard() {
      this.slideDirection = 'next';
      this.isFlipped = false; // Reset flip on next card
      this.goingToNextCard = true;

      // Logic to find the next unanswered card
      const currentAbsoluteIndex = this.filteredFlashcards.findIndex(card => (card.id || card.index) === (this.currentCard.id || this.currentIndex));
      let nextAvailableIndex = -1;

      for (let i = 1; i < this.filteredFlashcards.length; i++) {
          const checkIndex = (currentAbsoluteIndex + i) % this.filteredFlashcards.length;
          const checkCard = this.filteredFlashcards[checkIndex];
          const checkCardId = checkCard.id || checkIndex;
          if (!this.answeredCards[checkCardId]) {
              nextAvailableIndex = checkIndex;
              break;
          }
      }

      if (nextAvailableIndex !== -1) {
          this.currentIndex = nextAvailableIndex;
      } else if (Object.keys(this.answeredCards).length === this.filteredFlashcards.length) {
           // All cards answered, maybe show a message or reset?
           // For now, just stay on the last card or loop to first
           // this.currentIndex = 0; // Loop back to start
           console.log("Todas las tarjetas respondidas");
      } else {
         // Fallback if something goes wrong, go to next sequential if possible
         if (this.currentIndex < this.filteredFlashcards.length - 1) {
            this.currentIndex++;
         }
      }
      this.goingToNextCard = false;

    },


    prevCard() {
      if (this.currentIndex > 0) { // Simple previous for now
        this.slideDirection = 'prev';
        this.isFlipped = false; // Reset flip
        this.currentIndex--;
      }
    },

    handleKeyDown(event) {
      if (!this.isKeyboardNavigationActive || this.isEditing) return;

      switch (event.key) {
        case 'ArrowLeft':
             this.markAsIncorrect(); // Swipe left action
          break;
        case 'ArrowRight':
             this.markAsCorrect(); // Swipe right action
          break;
        case ' ': // Space
          event.preventDefault(); // Prevent scroll
          this.flipCard();
          break;
        case 'Enter': // Enter could also be used to flip or confirm
           if (this.isFlipped) {
             // Maybe mark as correct on enter? Or require arrows?
             // this.markAsCorrect(); 
           } else {
             this.flipCard();
           }
           break;
      }
    },

    activateKeyboardNavigation() {
      this.isKeyboardNavigationActive = true;
    },
    deactivateKeyboardNavigation() {
      this.isKeyboardNavigationActive = false;
    },

    goBack() {
      this.$router.push(`/app/document/${this.documentId}`);
    },

    toggleEditMode() {
      if (this.isEditing) {
        this.flashcards = this.originalFlashcards; // Cancel changes
        this.$emit('update:view-mode', 'cards'); // Go back to card view
      } else {
        this.originalFlashcards = JSON.parse(JSON.stringify(this.flashcards)); // Deep copy
        this.$emit('update:view-mode', 'list'); // Switch to list for editing
      }
      this.isEditing = !this.isEditing;
    },

    async saveFlashcards() {
      this.isLoading = true;
      try {
        // Filter out any potentially empty cards before saving
        const validFlashcards = this.flashcards.filter(card => card.question?.trim() && card.answer?.trim());
        
        const response = await this.axios.put(`/blocks/${this.blockId}/flashcards`, {
          flashcards: validFlashcards // Send only valid cards
        }, {
          headers: {
            Authorization: this.$root.token.value
          }
        });

        if (response.data.success) {
          this.$toast.success('Tarjetas guardadas correctamente');
          this.flashcards = validFlashcards; // Update local state with valid cards
          this.isEditing = false; // Exit edit mode
          this.$emit('update:view-mode', 'cards'); // Switch back to cards view
          // Optionally re-fetch or update computed properties if needed
           this.remainingCards = [...Array(this.flashcards.length).keys()];
           this.answeredCards = {};
           this.presentationCounts = {}; // Reset counts after saving
           this.flippedCards = {};
           this.correctCount = 0;
           this.incorrectCount = 0;
           this.currentIndex = 0;
           this.isFlipped = false;
           // Reset summary state
           this.isStudyComplete = false;
           this.firstTryCorrect = {};
           this.incorrectlyAnsweredCards = new Set();
           this.$nextTick(() => {
             this.incrementPresentationCount(this.currentIndex); // Recount current card if list not empty
           });
        } else {
          this.error = response.data.error || 'No se pudieron guardar las tarjetas';
          this.$toast.error('Error al guardar las tarjetas: ' + this.error);
        }
      } catch (err) {
        console.error('Error al guardar las tarjetas:', err);
        this.error = 'Error al guardar las tarjetas. Por favor, inténtalo de nuevo.';
        this.$toast.error('Error al guardar las tarjetas');
      } finally {
        this.isLoading = false;
      }
    },

    deleteFlashcard(index) {
      // Important: Use the index relative to the *currently displayed* flashcards array
      this.flashcards.splice(index, 1);
      // No need to adjust currentIndex here if in list view
      // If we delete the last card, ensure index is valid (though save handles this)
    },

    deleteCurrentFlashcard() {
       // Find the index in the main `flashcards` array corresponding to the `currentCard`
      const indexToDelete = this.flashcards.findIndex(card => (card.id || card.index) === (this.currentCard.id || this.currentIndex));
      if (indexToDelete !== -1) {
          this.flashcards.splice(indexToDelete, 1);
          // After deleting, need to update state carefully
          this.remainingCards = [...Array(this.flashcards.length).keys()]; // Rebuild remaining
          this.answeredCards = {}; // Reset answered
          this.flippedCards = {}; // Reset flipped
          this.correctCount = 0;
          this.incorrectCount = 0;
          // Go to the previous card index or 0 if it was the first
          const newIndex = Math.max(0, this.currentIndex - (indexToDelete <= this.currentIndex ? 1 : 0)); // Adjust index carefully
          this.currentIndex = newIndex;
          this.presentationCounts = {}; // Simplest is to reset counts on delete
          this.isFlipped = false;
          // Reset summary state
          this.isStudyComplete = false;
          this.firstTryCorrect = {};
          this.incorrectlyAnsweredCards = new Set();
          this.$nextTick(() => {
            this.incrementPresentationCount(this.currentIndex); // Recount current card if list not empty
          });
          // If the list becomes empty, handle that state
      }
    },

    printFlashcards() {
      const cards = this.flashcards;

      const commonStyles = `
        @media print {
          @page {
            size: A4;
            margin: 1cm;
          }
        }
        body {
          font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu, Arial, sans-serif;
          padding: 20px;
          max-width: 210mm;
          margin: 0 auto;
        }
        .header {
          text-align: center;
          margin-bottom: 30px;
          padding-bottom: 10px;
          border-bottom: 2px solid #4dd453;
        }
        .logo {
          color: #4dd453;
          font-size: 24px;
          font-weight: bold;
        }
        .footer {
          text-align: center;
          margin-top: 30px;
          font-size: 12px;
          color: #666;
        }
        @media print {
          .no-print {
            display: none;
          }
        }
        .cards-grid {
          width: 100%;
          border-collapse: collapse;
        }
        .card-row {
          border-bottom: 1px solid #E6E6E6;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .card-row:last-child {
          border-bottom: none;
        }
        .question-cell, .answer-cell {
          padding: 16px;
          vertical-align: top;
          width: 50%;
        }
        .question-cell {
          border-right: 1px solid #E6E6E6;
        }
        .question-content {
          font-weight: 500;
          color: #333;
        }
        .answer-content {
          color: #666;
        }
      `;

      // Estilos específicos para el modo cards
      const cardsStyles = `
        .card-row {
          height: 200px; /* Adjust as needed */
          page-break-inside: avoid;
          break-inside: avoid;
          border: 1px dashed #E6E6E6;
          margin: 16px 0;
          display: flex; /* Use flex for better alignment */
        }
        .question-cell, .answer-cell {
          height: 100%; /* Fill height */
          padding: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          font-size: 16px; /* Adjust font size */
        }
        .question-cell {
          border-right: 1px dashed #E6E6E6;
        }
        .card-row:last-child {
          margin-bottom: 0;
        }
        .card-row:first-child {
          margin-top: 0;
        }
        @media print {
          .cards-grid {
             display: block; /* Change from table for better page breaking */
          }
           .card-row {
             page-break-inside: avoid !important; /* Try forcing avoid */
             break-inside: avoid !important;
             width: 100%; /* Ensure full width */
             margin-bottom: 1cm; /* Add space between cards */
           }
          tr { /* Might not be applicable if not using table display */
            page-break-inside: avoid;
            break-inside: avoid;
          }
        }
      `;

      // Estilos específicos para el modo lista
      const listStyles = `
        .card-row {
          height: auto;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .question-cell, .answer-cell {
          height: auto;
          padding: 16px;
        }
        .question-content, .answer-content {
          text-align: left;
        }
        @media print {
          .cards-grid {
             display: block;
          }
           .card-row {
             page-break-inside: avoid !important;
             break-inside: avoid !important;
             width: 100%;
             margin-bottom: 0.5cm;
           }
          tr {
            page-break-inside: avoid;
            break-inside: avoid;
          }
        }
      `;

      // Use listStyles always for printing Q&A format
      const styles = listStyles;

      // Generate content as a list instead of table for better print flow
       const content = `
         <div class="cards-list-print">
           ${cards.map((card, index) => `
             <div class="card-item-print">
               <div class="card-number">${index + 1}</div>
               <div class="question-print">
                 <strong>Pregunta:</strong> ${card.question}
               </div>
               <div class="answer-print">
                 <strong>Respuesta:</strong> ${card.answer}
               </div>
             </div>
           `).join('')}
         </div>
       `;

      const printListStyles = `
        .cards-list-print {
           display: block;
        }
        .card-item-print {
           border: 1px solid #ccc;
           padding: 15px;
           margin-bottom: 15px;
           page-break-inside: avoid !important;
           break-inside: avoid !important;
           position: relative; /* For numbering */
        }
         .card-number {
           position: absolute;
           top: 5px;
           right: 10px;
           font-size: 12px;
           color: #999;
         }
        .question-print, .answer-print {
           margin-bottom: 10px;
        }
         .question-print strong, .answer-print strong {
            display: block;
            margin-bottom: 5px;
            color: #333;
         }
        @media print {
           body { font-size: 10pt; } /* Adjust base font size for print */
           .card-item-print { margin-bottom: 1cm; } /* Ensure spacing */
        }
      `;


      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Flashcards - ${this.documentTitle || 'sele.ai'}</title>
          <style>
            ${commonStyles}
            ${printListStyles} /* Use the new list styles */
          </style>
        </head>
        <body>
          <div class="header no-print">
            <div class="logo">sele.ai</div>
            <div>Flashcards: ${this.documentTitle}</div>
          </div>
          ${content}
          <div class="footer no-print">
            Generado por sele.ai - Tu asistente de estudio inteligente
          </div>
        </body>
        </html>
      `;

      // Create hidden iframe and print
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.display = 'none'; // Hide it
      document.body.appendChild(iframe);

      iframe.contentWindow.document.open();
      iframe.contentWindow.document.write(html);
      iframe.contentWindow.document.close();

      // Wait for content to load before printing
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // Remove iframe after a delay
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
       // Fallback if onload doesn't fire reliably
       setTimeout(() => {
          try {
             iframe.contentWindow.focus();
             iframe.contentWindow.print();
          } catch (e) {
             console.error("Error printing:", e);
          } finally {
             setTimeout(() => {
               if (iframe.parentNode === document.body) {
                 document.body.removeChild(iframe);
               }
             }, 1500);
          }
       }, 500); // Give it half a second to load
    },

    markAsCorrect() {
      const currentCardId = this.filteredFlashcards[this.currentIndex].id || this.currentIndex;
      const isFirstAttempt = !this.hasOwnProperty.call(this.firstTryCorrect, currentCardId);

      if (!this.answeredCards[currentCardId]) {
          this.$set(this.answeredCards, currentCardId, true);
          // this.correctCount++; // correctCount is now derived from answeredCards length
          this.showSwipeHelp = false;

          if (isFirstAttempt) {
              this.$set(this.firstTryCorrect, currentCardId, true);
          }
          this.$set(this.answeredCards, currentCardId, true);

          this.correctCount = Object.keys(this.answeredCards).length;
          this.incorrectCount = this.incorrectlyAnsweredCards.size;
          
          this.saveResults(); // Save partial results

          if (Object.keys(this.answeredCards).length === this.filteredFlashcards.length) {
              this.isStudyComplete = true;
              this.$emit('mark-item-as-completed');
              this.$emit('section-completed', { type: 'flashcards', score: this.firstTryCorrectPercentage });
              // this.saveResults(); // Already saved above, or save again if specific final state is needed
              
              this.cardActionAnimation = 'slideOutRight'; // Start animation
              setTimeout(() => {
                this.cardActionAnimation = null; // Reset for logical cleanup as view changes
              }, 300); // Animation duration
              return; 
          }
      }
      
      this.cardActionAnimation = 'slideOutRight'; // Start animation
      setTimeout(() => {
          this.$nextTick(() => {
              this.nextCard(); 
              this.cardActionAnimation = null; // Reset animation flag
          });
      }, 300); 
    },

    markAsIncorrect() {
        const currentCardId = this.filteredFlashcards[this.currentIndex].id || this.currentIndex;
        const isFirstAttempt = !this.hasOwnProperty.call(this.firstTryCorrect, currentCardId);

        if (isFirstAttempt) {
            this.$set(this.firstTryCorrect, currentCardId, false);
        }

        this.incorrectlyAnsweredCards.add(currentCardId);
        
        const currentFailures = this.failureCounts[currentCardId] || 0;
        this.$set(this.failureCounts, currentCardId, currentFailures + 1);
        
        if (this.answeredCards[currentCardId]) {
             this.$delete(this.answeredCards, currentCardId);
        }

        this.correctCount = Object.keys(this.answeredCards).length;
        this.incorrectCount = this.incorrectlyAnsweredCards.size;
        
        this.saveResults(); // Save partial results

        this.cardActionAnimation = 'slideOutLeft'; // Start animation
        setTimeout(() => {
            this.$nextTick(() => {
                this.nextCard(); 
                this.cardActionAnimation = null; // Reset animation flag
            });
        }, 300); 
    },

    restartStudy() {
      this.isStudyComplete = false;
      this.answeredCards = {};
      this.flippedCards = {};
      this.firstTryCorrect = {};
      this.incorrectlyAnsweredCards = new Set();
      this.failureCounts = {}; // Reiniciar contador de fallos
      this.correctCount = 0;
      this.incorrectCount = 0;
      this.currentIndex = 0;
      this.isFlipped = false;
      this.presentationCounts = {};
      this.showSwipeHelp = true; // Show help again
      // Optionally: Could re-randomize the order if needed
      // this.flashcards = shuffle(this.flashcards);
      // this.remainingCards = [...Array(this.flashcards.length).keys()];
      this.incrementPresentationCount(0); // Count the first card again
      // Ensure keyboard navigation is active if clicked outside previously
      this.activateKeyboardNavigation();
      
      // Eliminar los resultados guardados
      try {
        localStorage.removeItem(this.localStorageKey);
        this.hasStoredResults = false;
      } catch (e) {
        console.error('Error al eliminar los resultados guardados:', e);
      }
      
      // Emitir evento de reinicio
      this.$emit('quiz-retry');
    },
    
    // Método para guardar resultados
    saveResults() {
      // No guardar si está en modo edición
      if (this.isEditing) return;
      
      // Preparar los datos de resultados
      const resultData = {
        correctCount: this.correctCount,
        incorrectCount: this.incorrectCount,
        answeredCards: this.answeredCards,
        firstTryCorrect: this.firstTryCorrect,
        incorrectlyAnswered: Array.from(this.incorrectlyAnsweredCards),
        failureCounts: this.failureCounts, // Guardar el contador de fallos
        isStudyComplete: this.isStudyComplete,
        timestamp: new Date().getTime()
      };
      
      try {
        localStorage.setItem(this.localStorageKey, JSON.stringify(resultData));
        this.hasStoredResults = true;
        console.log('Resultados de flashcards guardados:', this.localStorageKey);
        
        // Si el estudio está completo o todas las tarjetas han sido respondidas, mostrar la vista de resumen
        if (this.resultData.isStudyComplete || 
            (Object.keys(this.answeredCards).length === this.flashcards.length && this.flashcards.length > 0)) {
          this.isStudyComplete = true;
          
          // Emitir evento de completado si no se había emitido antes
          this.$emit('mark-item-as-completed');
          this.$emit('section-completed', { type: 'flashcards', score: this.firstTryCorrectPercentage });
        }
      } catch (e) {
        console.error('Error al guardar los resultados:', e);
      }
    },
    
    // Método para verificar resultados guardados
    checkSavedResults() {
      try {
        const savedResults = localStorage.getItem(this.localStorageKey);
        
        if (savedResults) {
          const resultData = JSON.parse(savedResults);
          
          // Verificar si los datos no son demasiado antiguos (7 días)
          const now = new Date().getTime();
          const savedTime = resultData.timestamp || 0;
          const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 días en milisegundos
          
          if (now - savedTime > maxAge) {
            // Los datos son demasiado antiguos, eliminarlos
            localStorage.removeItem(this.localStorageKey);
            this.hasStoredResults = false;
            return;
          }
          
          // Guardar los datos temporalmente
          this.resultData = resultData;
          this.hasStoredResults = true;
          
          // Comprobar si todas las tarjetas ya han sido respondidas antes de cargar
          if (resultData.answeredCards && this.flashcards.length > 0 && 
              Object.keys(resultData.answeredCards).length === this.flashcards.length) {
            // Forzar el estado de estudio completo inmediatamente
            this.isStudyComplete = true;
          }
          
          // Cargar directamente los resultados guardados
          this.loadSavedResults();
        }
      } catch (e) {
        console.error('Error al verificar los resultados guardados:', e);
        this.hasStoredResults = false;
      }
    },
    
    // Método para cargar resultados guardados
    loadSavedResults() {
      if (!this.resultData) {
        console.error('No hay datos de resultados para cargar');
        return;
      }
      
      // Restaurar el estado desde los datos guardados
      this.correctCount = this.resultData.correctCount || 0;
      this.incorrectCount = this.resultData.incorrectCount || 0;
      
      // Restaurar tarjetas respondidas
      if (this.resultData.answeredCards) {
        this.answeredCards = this.resultData.answeredCards;
      }
      
      // Restaurar intentos de primer intento
      if (this.resultData.firstTryCorrect) {
        this.firstTryCorrect = this.resultData.firstTryCorrect;
      }
      
      // Restaurar tarjetas incorrectas
      if (this.resultData.incorrectlyAnswered && Array.isArray(this.resultData.incorrectlyAnswered)) {
        this.incorrectlyAnsweredCards = new Set(this.resultData.incorrectlyAnswered);
      }
      
      // Restaurar contador de fallos
      if (this.resultData.failureCounts) {
        this.failureCounts = this.resultData.failureCounts;
      }
      
      // Si el estudio estaba completo o todas las tarjetas han sido respondidas, mostrar la vista de resumen
      if (this.resultData.isStudyComplete || 
          (Object.keys(this.answeredCards).length === this.flashcards.length && this.flashcards.length > 0)) {
        this.isStudyComplete = true;
        
        // Emitir evento de completado si no se había emitido antes
        this.$emit('mark-item-as-completed');
        this.$emit('section-completed', { type: 'flashcards', score: this.firstTryCorrectPercentage });
      }
      
      console.log('Resultados de flashcards cargados:', this.localStorageKey);
    },

    // Añadir método para resetear el estado
    resetState() {
      this.isStudyComplete = false;
      this.answeredCards = {};
      this.flippedCards = {};
      this.firstTryCorrect = {};
      this.incorrectlyAnsweredCards = new Set();
      this.failureCounts = {};
      this.correctCount = 0;
      this.incorrectCount = 0;
      this.currentIndex = 0;
      this.isFlipped = false;
      this.presentationCounts = {};
      this.showSwipeHelp = true;
      this.error = null;
      this.isLoading = true;
      // Actualizar clave de localStorage para el nuevo bloque
      this.localStorageKey = this.storageKey;
    },
  }
};
</script>

<style scoped>
.flashcards-container {
  width: 100%;
}

.expand-button {
  border-radius: 10px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  font-size: 0.9rem !important;
  transition: filter 0.2s ease, transform 0.1s ease !important;
  padding: 0 24px !important;
  height: 36px !important;
  background-color: #4DD453 !important;
  box-shadow: 0 4px 0 #59A700 !important;
  margin-top: -10px;
  position: relative;
  color: #fff !important;
}

.flashcard {
  width: 100%;
  height: 100%;
  perspective: 1000px;
  box-shadow: none !important;
  transition: box-shadow 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-bottom: 60px;
  padding-top: 60px;
  cursor: pointer !important;
  /* Add user-select none again if needed, but test carefully */
   user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
   /* Prevent text selection during drag */
   -webkit-user-drag: none;
   /* Add transition for smooth reset */
  transition: transform 0.3s ease-out;
  will-change: transform; /* Optimize animation */
  /* Base transitions: reset transform after drag. Flip is on inner. Opacity handled by animating-out */
  transition: transform 0.3s ease-out;
}

.flashcard-inner {
  aspect-ratio: 3/2;
  cursor: pointer;
  width: 100%;
  max-width: 700px;
  border: 2px solid #E5E5E5;
  position: relative;
  transform-style: preserve-3d;
  border-radius: 24px !important;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  /* Remove swipe-related transitions/styles */
  transition: transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275); /* ONLY flip transition */
  /* Remove will-change if it was here */
}

/* Use class selector for flip */
.flashcard-inner.flipped {
  transform: rotateY(180deg);
}

/* Remove the old combined rule if it existed */
/* .flashcard.flipped .flashcard-inner { ... } */

.flashcard-front,
.flashcard-back {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 24px;
  background-color: white;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.flashcard-back {
  transform: rotateY(180deg);
}

.flashcard-content {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-size: 1.25rem;
  text-align: center;
}

.flashcard-footer {
  padding: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.85rem;
  color: #757575;
}

.difficulty-indicator {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 8px;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

/* .difficulty-indicator {
  background-color: #4DD453;
} */

/* .difficulty-indicator.easy {
  background-color: #4CAF50;
}

.difficulty-indicator.medium {
  background-color: #FF9800;
}

.difficulty-indicator.hard {
  background-color: #F44336;
} */

.card-counter {
  font-size: 1rem;
  color: #757575;
  font-weight: 500;
  width: 60px;
  text-align: center;
}

.flashcard-wrapper {
  height: 100%;
  width: 100%;
  position: relative;
  min-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.navigation-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50px;
}

.navigation-btn {
  transition: transform 0.2s ease;
  border-radius: 10px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  font-size: 0.9rem !important;
  transition: filter 0.2s ease, transform 0.1s ease !important;
  padding: 0 24px !important;
  height: 42px !important;
  box-shadow: 0 4px 0 #59A700 !important;
  position: relative;
  z-index: 2;
  min-width: 42px;
}


.navigation-btn-prev {
  margin-right: 20px;
}

.navigation-btn-next {
  margin-left: 20px;
}

.keyboard-hint {
  font-size: 0.8rem;
  color: #757575;
  align-items: center;
  top: 80px;
  left: 0;
  right: 0;
  z-index: 100;
}

kbd {
  display: inline-block;
  padding: 3px 6px;
  font-size: 0.8rem;
  color: #555;
  background-color: #f7f7f7;
  border: solid 1px #ccc;
  border-radius: 3px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
  margin: 0 2px;
}

/* Animaciones para el cambio de tarjeta */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fullscreen {
  height: 100%;
  margin-top: calc(50vh - 300px)
}

.fullscreen .v-card--link:before {
  background-color: transparent !important;
}

.flashcards-list {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.difficulty-badge {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 16px;
}

.difficulty-badge.easy {
  background-color: #4CAF50;
}

.difficulty-badge.medium {
  background-color: #FF9800;
}

.difficulty-badge.hard {
  background-color: #F44336;
}

.print-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
}

.edit-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 500 !important;
  position: absolute;
  top: -68px;
  right: 0;
  z-index: 2;
}

.delete-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 500 !important;
}

.edit-textarea {
  font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu, Arial, sans-serif !important;
}

@media print {
  .flashcards-container {
    padding: 0;
  }

  .print-btn,
  .edit-btn,
  .delete-btn {
    display: none;
  }
}

.primary-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 800 !important;
  font-size: 1.05rem !important;
  border-radius: 16px !important;
  transition: filter 0.2s ease, transform 0.1s ease !important;
  border: 2px solid #4dd453 !important;
  height: 40px !important;
  padding-left: 24px !important;
  padding-right: 24px !important;
  background: #4dd453 !important;
  box-shadow: 0 4px 0 #42b848 !important;
  color: white !important;
}

.cancel-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 800 !important;
  font-size: 1.05rem !important;
  border-radius: 16px !important;
  transition: filter 0.2s ease, transform 0.1s ease !important;
  height: 40px !important;
  padding-left: 24px !important;
  padding-right: 24px !important;
  background: #E8E8E8 !important;
  box-shadow: 0 4px 0 #b6b7b5 !important;
  color: #6E8071 !important;
  margin-right: 16px !important;
}

.evaluation-buttons {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  gap: 16px;
}

.evaluation-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
}

.error-btn {
  background-color: #F44336 !important;
  color: white !important;
  box-shadow: 0 4px 0 #D32F2F !important;
}

.success-btn {
  background-color: #4CAF50 !important;
  color: white !important;
  box-shadow: 0 4px 0 #388E3C !important;
}

.counter {
  font-weight: 600;
  border-radius: 6px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  font-size: 0.9rem;
}


.total-counter {
  background-color: rgba(128, 128, 128, 0.1); /* Light grey background */
  color: #808080; /* Grey text color */
  position: absolute;
  left: 0; /* Center horizontally */
  right: 0;
  margin: 0 auto;
  width: fit-content;
  top: 16px;
}

.incorrect-counter {
  background-color: rgba(244, 67, 54, 0.1);
  color: #D32F2F;
  position: absolute;
  left: 20px;
  top: 16px;
}

.correct-counter {
  background-color: rgba(76, 175, 80, 0.1);
  color: #388E3C;
  position: absolute;
  right: 60px;
  top: 16px;
}

.position-relative {
  position: relative;
}

.evaluation-buttons-container {
  display: flex;
  justify-content: center;
  gap: 16px;
}

/* Estilos para los estados de swipe */
.flashcard.swiping-left .flashcard-inner {
  border-color: #F44336;
  box-shadow: 0 8px 20px rgba(244, 67, 54, 0.3) !important;
  border-width: 3px !important;
  transition: all 0.3s cubic-bezier(0.2, 0.85, 0.45, 1.2) !important;
}

.flashcard.swiping-right .flashcard-inner {
  border-color: #4CAF50;
  box-shadow: 0 8px 20px rgba(76, 175, 80, 0.3) !important;
  border-width: 3px !important;
  transition: all 0.3s cubic-bezier(0.2, 0.85, 0.45, 1.2) !important;
}
.flashcard.not-swiping .flashcard-inner {
  border-color: #E5E5E5 !important;
}

/* Hacer las animaciones más suaves */
@keyframes slideLeft {
  0%, 100% {
    transform: translateX(0);
    opacity: 1;
  }
  50% {
    transform: translateX(-8px); /* Incrementar el desplazamiento */
    opacity: 0.6;
  }
}

@keyframes slideRight {
  0%, 100% {
    transform: translateX(0);
    opacity: 1;
  }
  50% {
    transform: translateX(8px); /* Incrementar el desplazamiento */
    opacity: 0.6;
  }
}

/* Animaciones más suaves para los indicadores */
.swipe-indicator {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.7); /* Slightly darker */
  color: white;
  padding: 12px 18px; /* Slightly larger */
  border-radius: 25px;
  display: flex;
  align-items: center;
  gap: 10px; /* More spacing */
  font-weight: 600; /* Bolder */
  z-index: 10;
  opacity: 0; /* Start hidden */
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out; /* Smooth fade/scale */
  pointer-events: none; /* Don't interfere with drag */
}

.isDragging .swipe-indicator {
    opacity: 1; /* Show when dragging */
    transform: translateY(-50%) scale(1.05); /* Slight scale effect */
}

.swipe-indicator.left {
  left: 25px; /* Adjust position */
  background-color: rgba(244, 67, 54, 0.85); /* Stronger color */
}

.swipe-indicator.right {
  right: 25px; /* Adjust position */
  background-color: rgba(76, 175, 80, 0.85); /* Stronger color */
}

/* Refine swipe hint styles */
.swipe-hint {
  font-size: 0.8rem;
  color: #757575;
  align-items: center;
  opacity: 0.8; /* Make it slightly less prominent */
  transition: opacity 0.3s ease;
}

/* Hide hint when help is dismissed */
.swipe-hint:not([style*="display: none"]) { /* Target only when visible */
   /* Add specific styles if needed when showSwipeHelp is true */
}

/* Adjust evaluation buttons container */
.evaluation-buttons-container {
  opacity: 1; /* Always visible when in DOM */
  transform: translateY(0); /* Normal position */
  transition: opacity 0.3s ease, transform 0.3s ease;
  pointer-events: auto; /* Always interactive when in DOM */
}

/* Show buttons smoothly when card is flipped */
/* This rule is no longer needed as v-if controls presence and base style above makes it visible */
/*
.flashcard:has(.flashcard-inner.flipped) + .evaluation-buttons-container {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto; 
}
*/

/* Style for the edit textareas */
.edit-textarea >>> .v-input__control > .v-input__slot {
    box-shadow: none !important;
    border: 1px solid #dcdcdc !important;
    border-radius: 8px !important;
    background-color: #f9f9f9 !important;
 }

 .edit-textarea >>> textarea {
     line-height: 1.5 !important;
 }

/* Style for animating out - REMOVED */
/*
.flashcard.animating-out {
  opacity: 0 !important;
  transform: translateX(0) rotate(0deg) !important; 
  pointer-events: none; 
  transition: opacity 0.3s ease-out, transform 0s !important;
  border: none !important;
}
*/

/* New styles for slide-out animations */
.flashcard.slide-out-left-active {
  animation: slideOutLeftAnimation 0.3s ease-out forwards;
}
.flashcard.slide-out-left-active .flashcard-inner {
  border-color: #F44336 !important;
  border-width: 3px !important;
}

.flashcard.slide-out-right-active {
  animation: slideOutRightAnimation 0.3s ease-out forwards;
}
.flashcard.slide-out-right-active .flashcard-inner {
  border-color: #4CAF50 !important;
  border-width: 3px !important;
}

@keyframes slideOutLeftAnimation {
  to {
    transform: translateX(-100%) rotate(-15deg);
    opacity: 0;
  }
}
@keyframes slideOutRightAnimation {
  to {
    transform: translateX(100%) rotate(15deg);
    opacity: 0;
  }
}

.presentation-chip {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 5;
  font-size: 0.75rem;
  height: 20px;
}

/* Estils per a la vista de resum */
.study-summary {
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  flex-grow: 1; /* Allow summary to grow */
  height: 100%; /* Take full available height */
  padding-top: 1.5rem; /* Equivalent to my-6 top */
  padding-bottom: 1.5rem; /* Equivalent to my-6 bottom */
}

.summary-card-container {
  display: flex;
  flex-direction: column;
  flex-grow: 1; /* Allow card inside summary to grow */
  height: 100%; /* Allow card to use the height from study-summary */
}

.flashcards-summary-list {
  flex-grow: 1; /* Allow the list to take available space */
  /* Remove fixed max-height */
}

.summary-card.incorrect-summary-card {
  border: 2px solid #F44336 !important; /* Vora vermella a l'esquerra */
}

.failures-badge {
  display: inline-block;
  margin-top: 8px;
  font-size: 0.85rem;
  background-color: #FFEBEE;
  color: #D32F2F;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
}

.incorrect-summary-card .font-rubik {
  color: #D32F2F; /* Texto en rojo oscuro para mejor legibilidad */
}
</style>